/**
 * Mutual Fund Name to ID Mapping
 * Centralized mapping for all mutual fund names and their corresponding IDs
 * Used across the application for API calls and mutual fund identification
 */

// Mutual Fund mapping: ID -> Full Name
const MUTUAL_FUND_ID_TO_NAME = {
  1: 'Parag Parikh Flexi Cap Fund Direct Growth',
  2: 'Kotak Multicap Fund Direct Growth',
  3: 'ICICI Prudential Large Cap Fund Direct Growth',
  4: 'ICICI Prudential Large & Mid Cap Direct',
  5: 'HDFC Mid Cap Fund Direct',
  6: 'Invesco India Smallcap Fund Direct',
  7: 'ICICI Prudential Dividend Yield Equity Fund Direct',
  8: 'Motilal Oswal BSE Enhanced Value Index Fund Direct',
  9: 'HDFC Focused Fund Direct',
  10: 'Parag Parikh ELSS Tax Saver Fund Direct',
  11: 'Axis Overnight Fund Direct',
  12: 'Axis Liquid Fund Direct',
  13: 'Bandhan Ultra Short Duration Fund Direct',
  14: 'Axis Money Market Fund Direct',
  15: 'Axis Short Duration Fund Direct',
  16: 'ICICI Prudential Corporate Bond Fund Direct',
  17: 'HDFC Banking and PSU Debt Fund Direct',
  18: 'Aditya Birla Sun Life Floating Rate Fund Direct',
  19: 'Parag Parikh Conservative Hybrid Fund Direct',
  20: 'ICICI Prudential Equity & Debt Fund Direct',
  21: 'ICICI Prudential Balanced Advantage Fund Direct',
  22: 'Nippon India Multi Asset Allocation Fund - Direct',
  23: 'Aditya Birla Sun Life Arbitrage Fund Direct',
  24: 'Edelweiss Equity Savings Fund Direct',
  25: 'Motilal Oswal NASDAQ 100 ETF',
  26: 'Mirae Asset NYSE FANG+ ETF Fund of Fund Direct'
}

// Mutual Fund mapping: ID -> ISIN Code
const MUTUAL_FUND_ID_TO_ISIN = {
  1: 'INF879O01027',
  2: 'INF174KA1HV3',
  3: 'INF109K016L0',
  4: 'INF109K011O5',
  5: 'INF179K01XQ0',
  6: 'INF205K013T3',
  7: 'INF109KA1UA0',
  8: 'INF247L01BF2',
  9: 'INF179K01VK7',
  10: 'INF879O01100',
  11: 'INF846K01N65',
  12: 'INF846K01CX4',
  13: 'INF194KA16R3',
  14: 'INF846K01Q62',
  15: 'INF846K01EI1',
  16: 'INF109K016B1',
  17: 'INF179KA1IZ7',
  18: 'INF209K01UX7',
  19: 'INF879O01175',
  20: 'INF109K01Y07',
  21: 'INF109K012B0',
  22: 'INF204KB19V4',
  23: 'INF209K01VP1',
  24: 'INF843K01KC8',
  25: 'INF247L01AP3',
  26: 'INF769K01HH0'
}

// Mutual Fund mapping: ID -> AMFI Code
const MUTUAL_FUND_ID_TO_AMFI = {
  1: '122639',
  2: '149185',
  3: '120586',
  4: '120596',
  5: '118989',
  6: '145137',
  7: '129312',
  8: '150518',
  9: '118950',
  10: '147481',
  11: '146675',
  12: '120389',
  13: '144173',
  14: '147567',
  15: '120510',
  16: '120692',
  17: '128629',
  18: '122646',
  19: '148958',
  20: '120251',
  21: '120377',
  22: '148457',
  23: '119526',
  24: '140347',
  25: '114984',
  26: '148928'
}

// Create reverse mapping: Name -> ID
const MUTUAL_FUND_NAME_TO_ID = {}
Object.entries(MUTUAL_FUND_ID_TO_NAME).forEach(([id, name]) => {
  MUTUAL_FUND_NAME_TO_ID[name] = parseInt(id)
})

// Create base name mappings (without "Fund Direct Growth" suffixes)
const MUTUAL_FUND_BASE_NAME_TO_ID = {}
Object.entries(MUTUAL_FUND_ID_TO_NAME).forEach(([id, fullName]) => {
  // Extract base name before "Fund"
  let baseName = fullName

  // Handle different fund naming patterns
  if (fullName.includes('Fund Direct Growth')) {
    baseName = fullName.split('Fund Direct Growth')[0].trim() + ' Fund'
  } else if (fullName.includes('Fund Direct')) {
    baseName = fullName.split('Fund Direct')[0].trim() + ' Fund'
  } else if (fullName.includes('Direct')) {
    baseName = fullName.split('Direct')[0].trim()
  } else if (fullName.includes('ETF')) {
    // ETFs might not follow the same pattern
    baseName = fullName
  }

  MUTUAL_FUND_BASE_NAME_TO_ID[baseName] = parseInt(id)

  // Also add without "Fund" suffix for more flexible matching
  const nameWithoutFund = baseName.replace(/\s+Fund$/, '').trim()
  if (nameWithoutFund !== baseName) {
    MUTUAL_FUND_BASE_NAME_TO_ID[nameWithoutFund] = parseInt(id)
  }
})

// Additional variations and aliases for better matching
const MUTUAL_FUND_ALIASES = {
  // Parag Parikh variations
  'Parag Parikh Flexi Cap': 1,
  'PPFCF': 1,
  'PP Flexi Cap': 1,
  'Parag Parikh ELSS': 10,
  'PP ELSS': 10,
  'Parag Parikh Conservative Hybrid': 19,
  'PP Conservative Hybrid': 19,

  // ICICI Prudential variations
  'ICICI Pru Large Cap': 3,
  'ICICI Large Cap': 3,
  'ICICI Prudential Large Mid Cap': 4,
  'ICICI Large Mid Cap': 4,
  'ICICI Pru Dividend Yield': 7,
  'ICICI Dividend Yield': 7,
  'ICICI Pru Corporate Bond': 16,
  'ICICI Corporate Bond': 16,
  'ICICI Pru Equity Debt': 20,
  'ICICI Equity Debt': 20,
  'ICICI Pru Balanced Advantage': 21,
  'ICICI Balanced Advantage': 21,

  // HDFC variations
  'HDFC Mid Cap': 5,
  'HDFC Focused': 9,
  'HDFC Banking PSU Debt': 17,
  'HDFC Banking and PSU': 17,

  // Axis variations
  'Axis Overnight': 11,
  'Axis Liquid': 12,
  'Axis Money Market': 14,
  'Axis Short Duration': 15,

  // Other variations
  'Kotak Multicap': 2,
  'Invesco Smallcap': 6,
  'Invesco India Smallcap': 6,
  'Motilal Oswal BSE Enhanced Value': 8,
  'MO BSE Enhanced Value': 8,
  'Bandhan Ultra Short': 13,
  'Aditya Birla Floating Rate': 18,
  'ABSL Floating Rate': 18,
  'Nippon Multi Asset': 22,
  'Nippon India Multi Asset': 22,
  'Aditya Birla Arbitrage': 23,
  'ABSL Arbitrage': 23,
  'Edelweiss Equity Savings': 24,
  'Motilal Oswal NASDAQ': 25,
  'MO NASDAQ 100': 25,
  'Mirae NYSE FANG': 26,
  'Mirae Asset NYSE FANG': 26
}

/**
 * Get full mutual fund name from SecurityName (for API calls that require full name)
 * @param {string} securityName - Security name from vehicle data
 * @returns {string|null} - Full mutual fund name or null if not found
 */
export function getFullMutualFundName(securityName) {
  if (!securityName) return null

  // First try to get the mutual fund ID
  const fundId = getMutualFundIdFromName(securityName)
  if (fundId) {
    // Return the full name from the mapping
    return MUTUAL_FUND_ID_TO_NAME[fundId] || null
  }

  return null
}

/**
 * Get mutual fund ID from fund name (with fuzzy matching)
 * @param {string} fundName - Mutual fund name or security name
 * @returns {number|null} - Mutual fund ID or null if not found
 */
export function getMutualFundIdFromName(fundName) {
  if (!fundName) return null

  const name = fundName.toString().trim()

  // Try exact match with full name
  if (MUTUAL_FUND_NAME_TO_ID[name]) {
    return MUTUAL_FUND_NAME_TO_ID[name]
  }

  // Try exact match with base name
  if (MUTUAL_FUND_BASE_NAME_TO_ID[name]) {
    return MUTUAL_FUND_BASE_NAME_TO_ID[name]
  }

  // Try exact match with aliases
  if (MUTUAL_FUND_ALIASES[name]) {
    return MUTUAL_FUND_ALIASES[name]
  }

  // Try partial matching (case insensitive)
  const nameLower = name.toLowerCase()

  // Check base names for partial matches
  for (const [baseName, id] of Object.entries(MUTUAL_FUND_BASE_NAME_TO_ID)) {
    const baseNameLower = baseName.toLowerCase()

    // If input name contains the base name or vice versa
    if (nameLower.includes(baseNameLower) || baseNameLower.includes(nameLower)) {
      // Additional check to avoid false positives
      if (nameLower.length >= 3 && baseNameLower.length >= 3) {
        return id
      }
    }
  }

  // Check aliases for partial matches
  for (const [alias, id] of Object.entries(MUTUAL_FUND_ALIASES)) {
    const aliasLower = alias.toLowerCase()
    if (nameLower.includes(aliasLower) || aliasLower.includes(nameLower)) {
      if (nameLower.length >= 3 && aliasLower.length >= 3) {
        return id
      }
    }
  }

  return null
}

/**
 * Get mutual fund name from ID
 * @param {number} fundId - Mutual fund ID
 * @returns {string|null} - Full mutual fund name or null if not found
 */
export function getMutualFundNameFromId(fundId) {
  if (!fundId) return null
  return MUTUAL_FUND_ID_TO_NAME[fundId] || null
}

/**
 * Get base mutual fund name from ID (without Direct Growth suffix)
 * @param {number} fundId - Mutual fund ID
 * @returns {string|null} - Base mutual fund name or null if not found
 */
export function getBaseMutualFundNameFromId(fundId) {
  const fullName = getMutualFundNameFromId(fundId)
  if (!fullName) return null

  // Extract base name
  if (fullName.includes('Fund Direct Growth')) {
    return fullName.split('Fund Direct Growth')[0].trim() + ' Fund'
  } else if (fullName.includes('Fund Direct')) {
    return fullName.split('Fund Direct')[0].trim() + ' Fund'
  } else if (fullName.includes('Direct')) {
    return fullName.split('Direct')[0].trim()
  } else {
    return fullName
  }
}

/**
 * Check if a mutual fund ID exists
 * @param {number} fundId - Mutual fund ID
 * @returns {boolean} - True if fund ID exists
 */
export function isValidMutualFundId(fundId) {
  return fundId && Object.prototype.hasOwnProperty.call(MUTUAL_FUND_ID_TO_NAME, fundId)
}

/**
 * Get all mutual fund IDs
 * @returns {number[]} - Array of all mutual fund IDs
 */
export function getAllMutualFundIds() {
  return Object.keys(MUTUAL_FUND_ID_TO_NAME).map(id => parseInt(id))
}

/**
 * Get all mutual fund names
 * @returns {string[]} - Array of all mutual fund names
 */
export function getAllMutualFundNames() {
  return Object.values(MUTUAL_FUND_ID_TO_NAME)
}

/**
 * Get all base mutual fund names (without Direct Growth suffixes)
 * @returns {string[]} - Array of all base mutual fund names
 */
export function getAllBaseMutualFundNames() {
  return Object.keys(MUTUAL_FUND_BASE_NAME_TO_ID)
}

/**
 * Get ISIN code from mutual fund ID
 * @param {number} fundId - Mutual fund ID
 * @returns {string|null} - ISIN code or null if not found
 */
export function getISINFromId(fundId) {
  if (!fundId) return null
  return MUTUAL_FUND_ID_TO_ISIN[fundId] || null
}

/**
 * Get AMFI code from mutual fund ID
 * @param {number} fundId - Mutual fund ID
 * @returns {string|null} - AMFI code or null if not found
 */
export function getAMFIFromId(fundId) {
  if (!fundId) return null
  return MUTUAL_FUND_ID_TO_AMFI[fundId] || null
}

/**
 * Get fund type from mutual fund ID (Equity/Debt/Hybrid)
 * @param {number} fundId - Mutual fund ID
 * @returns {string} - Fund type (Equity/Debt/Hybrid)
 */
export function getFundTypeFromId(fundId) {
  const fundName = getMutualFundNameFromId(fundId)
  if (!fundName) return 'Unknown'

  const nameLower = fundName.toLowerCase()

  // Debt funds
  if (nameLower.includes('overnight') || nameLower.includes('liquid') ||
      nameLower.includes('duration') || nameLower.includes('bond') ||
      nameLower.includes('debt') || nameLower.includes('money market') ||
      nameLower.includes('floating rate')) {
    return 'Debt'
  }

  // Hybrid funds
  if (nameLower.includes('hybrid') || nameLower.includes('balanced') ||
      nameLower.includes('arbitrage') || nameLower.includes('equity savings') ||
      nameLower.includes('multi asset') || nameLower.includes('conservative')) {
    return 'Hybrid'
  }

  // Default to Equity for most others
  return 'Equity'
}

/**
 * Search mutual funds by partial name
 * @param {string} searchTerm - Search term
 * @returns {Array} - Array of {id, name, baseName} objects
 */
export function searchMutualFunds(searchTerm) {
  if (!searchTerm || searchTerm.length < 2) return []

  const term = searchTerm.toLowerCase()
  const results = []

  Object.entries(MUTUAL_FUND_ID_TO_NAME).forEach(([id, fullName]) => {
    if (fullName.toLowerCase().includes(term)) {
      results.push({
        id: parseInt(id),
        name: fullName,
        baseName: getBaseMutualFundNameFromId(parseInt(id))
      })
    }
  })

  return results
}

// Export constants for direct use if needed
export {
  MUTUAL_FUND_ID_TO_NAME,
  MUTUAL_FUND_NAME_TO_ID,
  MUTUAL_FUND_BASE_NAME_TO_ID,
  MUTUAL_FUND_ALIASES,
  MUTUAL_FUND_ID_TO_ISIN,
  MUTUAL_FUND_ID_TO_AMFI
}
